import { useContext } from "react";
import { MdDelete, MdThumbUp } from "react-icons/md";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";

import styles from "./ChatBubble.module.css";
import { HTMLRenderer, StyledButton } from "../components";
import { LangContext } from "../contexts/langContext";
import { getButtonPalletFromCorrectOrIncorrect } from "../util/getButtonPalletFromCorrectOrIncorrect";
import useShowingCorrectAnswer from "../hooks/useShowingCorrectAnswer";
import { SelectAnswerContext } from "../contexts/selectAnswerContext";
import { ContentIsScamOrNot, TranslationData } from "@common";
import useTranslatePageText from "@natklok/hooks/useTranslatePageText";

interface RenderProps extends ContentIsScamOrNot {
  readOnly?: boolean;
  showCorrect?: boolean;
}

const ContentIsScamOrNotComponent: React.FC<{
  [key in keyof RenderProps]: RenderProps[key];
}> = ({ isScam, content, type, from, readOnly = false, showCorrect = false }) => {
  const { onSelectAnswer } = useContext(SelectAnswerContext);
  const translatePageText = useTranslatePageText();
  const showingCorrectAnswer = useShowingCorrectAnswer();
  const displayCorrectAnswers = showCorrect || showingCorrectAnswer;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <>
      <div
        style={{
          background: "white",
          borderRadius: 8,
          padding: isMobile ? "16px 12px" : "42px 12px",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {type === "sms" && <SMSContent content={content} />}
        {type === "email" && <EmailContent from={from} content={content} />}
      </div>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <StyledButton
          hasCooldown
          fullWidth
          variant="primary"
          size={isMobile ? "small" : undefined}
          palette={getButtonPalletFromCorrectOrIncorrect(
            displayCorrectAnswers,
            isScam === "correct" ? "correct" : "incorrect"
          )}
          style={{
            maxWidth: 220,
            margin: "12px 4px",
          }}
          onClick={() => {
            const answerStatValues = {
              val1: 0,
              val2: isScam === "correct" ? 1 : 0,
              val3: isScam === "incorrect" ? 0 : 1,
            };
            onSelectAnswer(isScam === "correct" ? "correct" : "incorrect", answerStatValues);
          }}
          label={translatePageText("general.scam")}
          icon={<MdDelete style={{ fontSize: 20 }} />}
          disabled={readOnly || false}
        />
        <StyledButton
          hasCooldown
          fullWidth
          variant="primary"
          size={isMobile ? "small" : undefined}
          palette={getButtonPalletFromCorrectOrIncorrect(
            displayCorrectAnswers,
            isScam === "correct" ? "incorrect" : "correct"
          )}
          style={{
            maxWidth: 220,
            margin: "12px 4px",
          }}
          onClick={() => {
            const answerStatValues = {
              val1: 0,
              val2: isScam === "incorrect" ? 1 : 0,
              val3: isScam === "correct" ? 0 : 1,
            };
            onSelectAnswer(isScam === "correct" ? "incorrect" : "correct", answerStatValues);
          }}
          label={translatePageText("general.notScam")}
          icon={<MdThumbUp style={{ fontSize: 20 }} />}
          disabled={readOnly || false}
        />
      </div>
    </>
  );
};

const EmailContent: React.FC<{ content: TranslationData; from: string }> = ({ content, from }) => {
  const { translateTranslationData } = useContext(LangContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <div style={{ display: "flex" }}>
      {!isMobile && (
        <div
          style={{
            marginRight: 16,
            background: "green",
            borderRadius: 200,
            padding: 8,
            width: 40,
            height: 40,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {from.slice(0, 1)}
        </div>
      )}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography variant="body2" style={{ color: "rgba(0,0,0,0.4)" }}>
          <span style={{ margin: 0, color: "rgba(0,0,0,0.8)" }}>Från:</span> {from}
        </Typography>
        <Typography
          marginTop={0}
          variant="caption"
          style={{ color: "rgba(0,0,0,0.4)", margin: "0 0 6px 0" }}
        >
          {new Date().toLocaleString()}
        </Typography>
        <Typography variant="body2" style={{ color: "rgba(0,0,0,0.4)", margin: "0 0 6px 0" }}>
          <span style={{ color: "rgba(0,0,0,0.8)" }}>Till:</span> Dig
        </Typography>

        <Box width="100%" height="1px" bgcolor="lightgray" marginBottom={1} />
        <HTMLRenderer
          style={{
            color: "black",
            maxWidth: "100%",
            fontSize: isMobile ? 18 : undefined,
          }}
        >
          {translateTranslationData(content)}
        </HTMLRenderer>
      </div>
    </div>
  );
};

const SMSContent: React.FC<{ content: TranslationData }> = ({ content }) => {
  const { translateTranslationData, translatePageText } = useContext(LangContext);

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
      }}
    >
      <p style={{ color: "rgba(0,0,0,0.4)", fontSize: 14, margin: "0 0 6px 0" }}>
        <span style={{ color: "rgba(0,0,0,0.5)" }}>{translatePageText("general.today")}</span> 12:42
      </p>
      <div className={`${styles.yours} ${styles.messages}`}>
        <div className={`${styles.message} ${styles.last}`}>
          <HTMLRenderer>{translateTranslationData(content)}</HTMLRenderer>
        </div>
      </div>
    </div>
  );
};

export default ContentIsScamOrNotComponent;
